import { Injectable, inject } from '@angular/core'
import { HttpClient, HttpParams } from '@angular/common/http'

import { Observable, catchError, map } from 'rxjs'

import { environment } from '../../environments/environment'

import { ErrorService } from './error.service'

import { RootInjectorGuard } from '../guards/root-injector.guard'
import {
  Ingredient,
  IngredientCreate,
  IngredientEdit,
  IngredientListElement,
  IngredientListFilter,
  IngredientListResult,
} from '../types/ingredients'

@Injectable({
  providedIn: 'root',
})
export class IngredientService extends RootInjectorGuard {
  private http = inject(HttpClient)
  private errorService = inject(ErrorService)

  private URL = environment.baseUrl + 'ingredients'

  constructor() {
    super(IngredientService)
  }

  /**
   * Calls the autocomplete ingredient filter API.
   *
   *
   * @param {string} keyword - The search term used to filter ingredients.
   * @returns {Observable<Ingredient[]>} An Observable that emits an array of matching Ingredient objects.
   * @throws Will be caught and handled by the errorService if an HTTP error occurs.
   */
  autoCompleteIngredients(keyword: string): Observable<Ingredient[]> {
    let params = new HttpParams()
    params = params.set('keyword', keyword)
    return this.http
      .get<Ingredient[]>(`${this.URL}/search`, {
        params: params,
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Retrieves list of ingredients in the system
   * @param {number} page The current page number
   * @param {IngredientListFilter} filter The filter values
   * @returns {Observable<IngredientListResult>} Results
   */
  getIngredientList(
    page: number,
    filter: IngredientListFilter,
  ): Observable<IngredientListResult> {
    let params = new HttpParams()
    params = params.set('page', page)
    if (filter.name != null) {
      params = params.set('name', filter.name)
    }

    if (filter.casNumber != null) {
      params = params.set('casNumber', filter.casNumber)
    }

    return this.http
      .get<IngredientListResult>(this.URL, {
        params,
      })
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to create an ingredient
   * @param {IngredientCreate} ingredient The ingredient to create
   * @returns {Observable<void>}
   */
  create(ingredient: IngredientCreate): Observable<void> {
    return this.http.post<void>(this.URL, ingredient).pipe(
      map((res: void) => {
        return res
      }),
      catchError(this.errorService.handleError),
    )
  }

  /**
   * Gets an ingredient by id
   * @param {string} id The ingredient id
   * @returns {Observable<IngredientListElement>} Result
   */
  getById(id: string): Observable<IngredientListElement> {
    return this.http
      .get<IngredientListElement>(this.URL + `/${id}`)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends the request to update an ingredient
   * @param {IngredientEdit} updates The ingredient updates
   * @returns {Observable<void>}
   */
  edit(id: string, updates: IngredientEdit): Observable<void> {
    return this.http
      .put<void>(this.URL + `/${id}`, updates)
      .pipe(catchError(this.errorService.handleError))
  }

  /**
   * Sends request to import ingredients
   * @param {FormData} data The form data containing the CSV and template ID
   * @returns {Observable<void>} Response from the backend
   */
  importIngredients(data: FormData): Observable<void> {
    return this.http
      .post<void>(this.URL + '/import', data)
      .pipe(catchError(this.errorService.handleError))
  }
}
